import React, { useEffect } from 'react'
import { graphql } from 'gatsby'
import Seo from '@components/Seo'
import { useStore } from '@Store/'
import { setHeaderColor, setShowHeaderBackground } from '@Store/'
import { colors } from '@styles/vars/colors.style'
import Hero from '@components/_Investors/Hero'
import Highlights from '@components/_Investors/Highlights'
import Pages from '@components/_Investors/Pages'
import NewSection from '@components/_Investors/NewSection'
import RichText from '@components/RichText'
import Reports from '@components/Reports'
import RegulatoryNewsSubscribers from '@components/_Investors/RegulatoryNewsSubscribers'
import RegulatoryNews2 from '@components/_Investors/RegulatoryNews2'
import RegulatoryNews from '@components/_Investors/RegulatoryNews'
import Research from '@components/Research'

const InvestorsPage = ({ data }) => {
  const [, dispatch] = useStore()
  const {
    seo,
    slug,
    heroTitle,
    heroSummary,
    heroText,
    heroImage,
    highlightsTitle,
    highlights,
    investorOverview,
    ourBoardAdvisors,
    significantShareholders,
    aimRule26,
    accessToResearch,
    additionalTabs,
    corporateGovernanceCode
  } = data.contentfulInvestors
  const regulatoryNews = data.allContentfulArticle.nodes
  const reports = data.reportsAndPresentations.nodes
  const documents = data.documentsAndCirculars.nodes
  const acquisitions = data.acquisitions.nodes

  useEffect(() => {
    setHeaderColor(dispatch, colors.charcoal, colors.mist);
    setShowHeaderBackground(dispatch, true);
  }, [dispatch])

  const stringToSlug = (str) => {
      return str
        .trim()
        .toLowerCase()
        .replace(/ /g, '_')
        .replace(/[^\w-]+/g, '');
  }

  const getAddionalOptions = () => {
    let tabs = []
    if(additionalTabs) {
      additionalTabs.map((item, index) => {
        tabs.push({label: item.title, value: stringToSlug(item.title), content: <NewSection links={item.acceptFiles} content={item.tabContent} slug={stringToSlug(item.title)} key={index}/>});
      });
    }
    return tabs;
  }

  const options = [
    {
      label: 'Investor Overview',
      value: 'investor-overview',
      content: <RichText content={investorOverview} />,
    },
    {
      label: 'Our Board & Advisors',
      value: 'our-board-advisors',
      content: <RichText content={ourBoardAdvisors} />,
    },
    {
      label: 'Regulatory News',
      value: 'regulatory-news',
      content: <RegulatoryNews2 />,
    },
    {
      label: 'Regulatory News Alerts',
      value: 'regulatory-news-alert',
      content: <RegulatoryNewsSubscribers />,
    },
    {
      label: 'Reports & Presentations',
      value: 'reports-presentations',
      content: <Reports content={reports} />,
    },
    {
      label: 'Shareholder Information',
      value: 'shareholder-information',
      content: <RichText content={significantShareholders} />,
    },
    {
      label: 'AIM Rule 26',
      value: 'aim-rule-26',
      content: <RichText content={aimRule26} />,
    },
    {
      label: 'Access to Research',
      value: 'access-to-research',
      content: <Research content={accessToResearch} />,
    },
    {
      label: 'Shareholder Documents',
      value: 'documents-circulars',
      content: <Reports content={documents} />,
    },
    {
      label: 'Corporate Governance Code',
      value: 'corporate-governance-code',
      content: <RichText content={corporateGovernanceCode} />,
    },
    ...getAddionalOptions()
  ]

  return (
    <>
      <Seo data={seo} slug={slug} />

      <Hero
        title={heroTitle}
        textA={heroSummary}
        textB={heroText.heroText}
        image={heroImage}
      />

      <Highlights title={highlightsTitle} items={highlights} />

      <Pages options={options} />

    </>
  )
}

export default InvestorsPage

export const pageQuery = graphql`query InvestorsPage($slug: String!) {
  reportsAndPresentations: allContentfulReport(
    filter: {metadata: {tags: {elemMatch: {contentful_id: {eq: "reportsPresentations"}}}}}
    sort: {date: DESC}
  ) {
    nodes {
      date(formatString: "MMMM YYYY")
      formattedTitle {
        raw
      }
      pdfDownload {
        file {
          url
        }
      }
    }
  }
  documentsAndCirculars: allContentfulReport(
    filter: {metadata: {tags: {elemMatch: {contentful_id: {eq: "documentsCirculars"}}}}}
    sort: {date: DESC}
  ) {
    nodes {
      date(formatString: "MMMM YYYY")
      formattedTitle {
        raw
      }
      pdfDownload {
        file {
          url
        }
      }
    }
  }
  allContentfulArticle(
    filter: {metadata: {tags: {elemMatch: {contentful_id: {eq: "regulatoryNews"}}}}}
    sort: {date: DESC}
  ) {
    nodes {
      slug
      formattedTitle {
        raw
      }
      date(formatString: "MMMM YYYY")
      externalUrl
    }
  }
  acquisitions: allContentfulArticle(
    filter: {metadata: {tags: {elemMatch: {contentful_id: {eq: "acquisitions"}}}}}
    sort: {date: DESC}
  ) {
    nodes {
      slug
      formattedTitle {
        raw
      }
      date(formatString: "MMMM YYYY")
      externalUrl
    }
  }
  contentfulInvestors(slug: {eq: $slug}) {
    seo {
      ...SEO
    }
    slug
    heroTitle {
      raw
    }
    heroSummary
    heroText {
      heroText
    }
    heroImage {
      ...Image
    }
    highlightsTitle
    highlights {
      illustration {
        ...Image
      }
      title
      description {
        description
      }
    }
    investorOverview {
      raw
    }
    ourBoardAdvisors {
      raw
      references {
        ...References
      }
    }
    significantShareholders {
      raw
    }
    aimRule26 {
      raw
    }
    accessToResearch {
      raw
    }
    historicalPerformance {
      raw
    }
    corporateGovernanceCode {
      raw
    }
    additionalTabs {
      title
      acceptFiles {
        ...Image
        title
      }
      tabContent {
        raw
      }
    }
    subscribeText {
      raw
    }
    subscribeImage {
      ...Image
    }
  }
}`
